import Box from "@material-ui/core/Box";
import InputAdornment from "@material-ui/core/InputAdornment";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import MuiTextField, { TextFieldProps as MuiTextFieldProps } from "@material-ui/core/TextField";
import DownIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import UpIcon from "@material-ui/icons/KeyboardArrowUpRounded";
import clsx from "clsx";
import React, { useCallback } from "react";
import NumberFormat, { NumberFormatProps, NumberFormatValues } from "react-number-format";
import { useFocus } from "../../hook";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { ifDefined } from "../../util";
import { EndAdornmentContent, EndAdornmentContentProps } from "../EndAdornmentContent";
import { HelperTextWithCounter } from "../HelperTextWithCounter";
import { IconButton } from "../../interaction";

export type InputFormatProps = Omit<NumberFormatProps, "customInput" | "defaultValue" | "displayType" | "getInputRef" | "isNumericString" | "onValueChange" | "value">;

export interface NumberFieldProps
    extends Omit<MuiTextFieldProps, "color" | "defaultValue" | "multiline" | "onChange" | "rows" | "rowsMax" | "select" | "SelectProps" | "size" | "type" | "value">,
        HasTestId {
    /**
     * Whether the arrow up and down buttons should be displayed. Defaults to `undefined` which is interpreted as `false`.
     */
    disableArrowButtons?: boolean;
    /**
     * Whether the clear button should be displayed if the field is `focused` and non-empty. Defaults to `undefined` which is interpreted as `false`.
     */
    disableClearButton?: boolean;
    /**
     * Whether the lock icon should be displayed if the field is `disabled`. Defaults to `undefined` which is interpreted as `false`.
     */
    disableLockIcon?: boolean;
    /**
     * Don't display the validation icon. Defaults to `undefined` which is interpreted as `false` (= validation icon is shown if text is not empty depending on `error` property).
     */
    disableValidationIcon?: boolean;
    /**
     * For the available properties, see https://github.com/s-yadav/react-number-format/tree/v4.4.4#props, whereby `getInputRef`, `onValueChanged`, `value` are used internally
     * as well as `customInput`, `defaultValue`, `displayType` and `isNumericString` is not necessary.
     */
    InputFormatProps?: InputFormatProps;
    /**
     * Props applied to the Input element. It will be a `FilledInput` but without `endAdornment` which is customized completely
     */
    InputProps?: Omit<NonNullable<MuiTextFieldProps["InputProps"]>, "endAdornment">;
    /**
     * Every field should have a `label`. Use `""` (= empty string) if really none should be displayed.
     */
    label: MuiTextFieldProps["label"];
    maxLength?: number;
    onChange: (newValue: number | undefined) => void;
    /**
     * Every field should have a `placeholder`. Use `""` (= empty string) if really none should be displayed.
     */
    placeholder: MuiTextFieldProps["placeholder"];
    /**
     * Step by which the decrement and increment buttons adjust the current `value` which is sent to `onChange`.
     */
    step?: number;
    value: number | undefined;
}

export const NumberField: PlainFunctionComponent<NumberFieldProps> = React.forwardRef(
    (
        {
            disabled,
            disableArrowButtons,
            disableClearButton,
            disableLockIcon,
            disableValidationIcon,
            error,
            helperText,
            InputFormatProps,
            InputProps,
            maxLength,
            onBlur,
            onChange,
            onFocus,
            required,
            step = 1,
            testId,
            value,
            ...props
        },
        ref,
    ) => {
        const { focus, handleBlur, handleFocus } = useFocus(false, onBlur, onFocus);

        const handleChange = useCallback(
            (values: NumberFormatValues) => {
                onChange(values.floatValue); // see documentation https://github.com/s-yadav/react-number-format/tree/v4.4.4#values-object (for empty input field it is `undefined`)
            },
            [onChange],
        );
        const handleClear = useCallback(() => {
            onChange(undefined);
        }, [onChange]);

        const enhancedInputProps: MuiTextFieldProps["InputProps"] = {
            ...InputProps,
            className: clsx(InputProps?.className, { required }),
            endAdornment: (
                <EndAdornment
                    disabled={disabled}
                    disableArrowButtons={disableArrowButtons}
                    disableClearButton={disableClearButton}
                    disableLockIcon={disableLockIcon}
                    disableValidationIcon={disableValidationIcon}
                    error={error}
                    focus={focus}
                    onClear={handleClear}
                    onChange={onChange}
                    step={step}
                    value={value}
                />
            ),
            inputComponent: CustomNumberFormat as PlainFunctionComponent<unknown>,
            inputProps: {
                ...InputProps?.inputProps,
                ...InputFormatProps,
            },
        };

        return (
            <MuiTextField
                data-testid={testId}
                disabled={disabled}
                error={error}
                fullWidth={true}
                helperText={<HelperTextWithCounter disabled={disabled} helperText={helperText} length={value ? value.toString().length : 0} maxLength={maxLength} />}
                InputProps={enhancedInputProps}
                onBlur={handleBlur}
                onChange={(handleChange as unknown) as MuiTextFieldProps["onChange"]} // event now comes from `NumberInput` / `CustomNumberFormat` directly
                onFocus={handleFocus}
                ref={ref}
                required={required}
                // type="number" // <- does not work with `NumberInput` / `CustomNumberFormat`
                value={value ?? ""} // empty string ensures that "clearing" works for internally used `input` from `CustomNumberFormat`
                {...props}
            />
        );
    },
);

interface CustomNumberFormatProps extends InputFormatProps {
    inputRef?: NumberFormatProps["getInputRef"];
    onChange?: NumberFormatProps["onValueChange"];
}

const CustomNumberFormat: PlainFunctionComponent<CustomNumberFormatProps> = ({ inputRef, onChange, ...props }) => (
    <NumberFormat getInputRef={inputRef} onValueChange={onChange} {...props} />
);

interface EndAdornmentProps extends EndAdornmentContentProps {
    disableArrowButtons: boolean | undefined;
    onChange: (newValue: number | undefined) => void;
    step: number;
    value: number | undefined;
}

const EndAdornment: PlainFunctionComponent<EndAdornmentProps> = ({ disabled, disableArrowButtons, onChange, step, value, ...props }) => {
    const dontGrabFocusOnMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    // use native decrement and increment, get new value and forward it to on change
    const handleDecrement = useCallback(() => {
        onChange(ifDefined(value, (v) => v - step, -1));
    }, [onChange, step, value]);
    const handleIncrement = useCallback(() => {
        onChange(ifDefined(value, (v) => v + step, 1));
    }, [onChange, step, value]);

    const classes = useStyles();
    return (
        <InputAdornment position="end">
            <EndAdornmentContent disabled={disabled} value={value} {...props} />
            {disabled ? ( // replacing with `||` will cause console error "Invalid prop `children` supplied to `ForwardRef(InputAdornment)`, expected a ReactNode."
                <></>
            ) : (
                !disableArrowButtons && (
                    <Box className={classes.arrowBox}>
                        <IconButton aria-label="increment" className={classes.arrowButton} disabled={disabled} onClick={handleIncrement} onMouseDown={dontGrabFocusOnMouseDown}>
                            <UpIcon />
                        </IconButton>
                        <IconButton aria-label="decrement" className={classes.arrowButton} disabled={disabled} onClick={handleDecrement} onMouseDown={dontGrabFocusOnMouseDown}>
                            <DownIcon />
                        </IconButton>
                    </Box>
                )
            )}
        </InputAdornment>
    );
};

const useStyles = makeStyles(
    createStyles({
        arrowBox: {
            display: "flex",
            flexDirection: "column",
            marginLeft: 2,
            marginRight: 12,
        },
        arrowButton: {
            padding: 0,
        },
    }),
);
